/** Versioned authoring contract shared by Shadow Realm and publication. */
import { z } from "zod";

export const edgeSourceTypes = [
	"rtsp_camera",
	"axis_air_quality",
	"axis_sound_pressure",
	"axis_scene_events",
	"homeassistant_lock",
	"homeassistant_contact",
	"homeassistant_event",
	"milestone_event",
] as const;
export const edgeSecretFields = {
	integration: ["auth_token", "password", "onb_password"],
	source: ["rtsp_url", "sensor_password"],
} as const;
export const edgeIdentifier = z
	.string()
	.regex(/^[A-Za-z0-9_-][A-Za-z0-9_.-]{0,127}$/);
const label = z
	.string()
	.trim()
	.min(1)
	.max(120)
	.refine((value) => !/[\r\n]/.test(value));
const optionalText = z.string().max(1024).nullish();
const host = z
	.string()
	.trim()
	.min(1)
	.max(253)
	.regex(/^[A-Za-z0-9._-]+$/);
export const edgeSecretEditSchema = z.discriminatedUnion("operation", [
	z.object({ operation: z.literal("keep"), version: z.uuid() }).strict(),
	z
		.object({
			operation: z.literal("replace"),
			value: z.string().min(1).max(8192),
		})
		.strict(),
	z.object({ operation: z.literal("remove") }).strict(),
]);
export type EdgeSecretEdit = z.infer<typeof edgeSecretEditSchema>;
export const edgeSourceFieldsSchema = z
	.object({
		audio_enabled: z.boolean().default(false),
		ca_bundle_pem: z.string().max(65536).nullish(),
		ha_entity_id: optionalText,
		integration_id: z.uuid().nullish(),
		name: label,
		segment_duration: z.number().int().min(1).max(300),
		segment_format: z.enum(["mp4", "ts", "json"]),
		sensor_event_topic: optionalText,
		sensor_host: host.nullish(),
		sensor_id: edgeIdentifier,
		sensor_port: z.number().int().min(1).max(65535).default(443),
		sensor_username: optionalText,
		skip_upload: z.boolean().default(false),
		source_type: z.enum(edgeSourceTypes),
		tls_verify: z.boolean().default(true),
		video_bitrate: z.number().int().min(100).max(50000).nullish(),
		video_fps: z.number().int().min(1).max(60).nullish(),
		video_passthrough: z.boolean().default(false),
		video_resolution: z
			.string()
			.regex(/^\d+x\d+$/)
			.nullish(),
		video_sensor_id: edgeIdentifier.nullish(),
		zone_id: edgeIdentifier.nullish(),
	})
	.strict();
export const edgeIntegrationFieldsSchema = z
	.object({
		ca_bundle_pem: z.string().max(65536).nullish(),
		host,
		integration_type: z.enum(["homeassistant", "milestone"]),
		name: label,
		onb_host: host.nullish(),
		onb_port: z.number().int().min(1).max(65535).default(554),
		onb_username: optionalText,
		port: z.number().int().min(1).max(65535),
		tls_verify: z.boolean().default(true),
		username: optionalText,
	})
	.strict();
const source = z
	.object({
		desiredState: z.enum(["running", "stopped"]),
		fields: edgeSourceFieldsSchema,
		id: z.uuid(),
		secrets: z
			.partialRecord(z.enum(edgeSecretFields.source), edgeSecretEditSchema)
			.default({}),
	})
	.strict();
const integration = z
	.object({
		fields: edgeIntegrationFieldsSchema,
		id: z.uuid(),
		secrets: z
			.partialRecord(z.enum(edgeSecretFields.integration), edgeSecretEditSchema)
			.default({}),
	})
	.strict();
const has = (edit: EdgeSecretEdit | undefined) =>
	!!edit && edit.operation !== "remove";
export const edgeConfigurationDraftSchema = z
	.object({
		integrations: z.array(integration).max(100),
		schemaVersion: z.literal(1),
		sources: z.array(source).max(200),
	})
	.strict()
	.superRefine((content, ctx) => {
		const fail = (path: (number | string)[], message: string) =>
			ctx.addIssue({ code: "custom", message, path });
		const ids = new Set<string>();
		const sensors = new Set<string>();
		const integrations = new Map(
			content.integrations.map((item) => [item.id, item]),
		);
		content.integrations.forEach((item, i) => {
			const f = item.fields;
			const path = ["integrations", i];
			if (ids.has(item.id)) fail(path, "Duplicate stable identifier");
			ids.add(item.id);
			if (f.integration_type === "homeassistant") {
				if (!has(item.secrets.auth_token))
					fail(path, "Home Assistant requires a token");
				if (
					f.username ||
					has(item.secrets.password) ||
					f.onb_host ||
					f.onb_username ||
					has(item.secrets.onb_password) ||
					f.ca_bundle_pem
				)
					fail(
						path,
						"Home Assistant does not support password, bridge, or CA bundle fields",
					);
			} else {
				if (
					f.port !== 443 ||
					!f.username ||
					!has(item.secrets.password) ||
					has(item.secrets.auth_token)
				)
					fail(
						path,
						"Milestone requires port 443 and username/password authentication",
					);
				if (
					(f.onb_host || f.onb_username || has(item.secrets.onb_password)) &&
					!(f.onb_host && f.onb_username && has(item.secrets.onb_password))
				)
					fail(path, "Complete the video bridge host and credentials");
			}
			if (f.ca_bundle_pem && !f.tls_verify)
				fail(path, "A CA bundle requires TLS verification");
		});
		content.sources.forEach((item, i) => {
			const f = item.fields;
			const path = ["sources", i];
			if (ids.has(item.id)) fail(path, "Duplicate stable identifier");
			ids.add(item.id);
			if (sensors.has(f.sensor_id))
				fail(path, "Sensor identifiers must be unique within an installation");
			sensors.add(f.sensor_id);
			if (
				f.ca_bundle_pem &&
				(f.source_type !== "milestone_event" || !f.tls_verify)
			)
				fail(
					path,
					"CA bundles require a Milestone source with TLS verification",
				);
			if (f.source_type === "rtsp_camera") {
				if (!f.skip_upload && (!f.zone_id || f.segment_format !== "mp4"))
					fail(
						path,
						"Video delivery requires a zone identifier and mp4 segments",
					);
				if (
					!has(item.secrets.rtsp_url) ||
					!["mp4", "ts"].includes(f.segment_format)
				)
					fail(
						path,
						"RTSP sources require a stream URL and mp4 or ts segments",
					);
				const url = item.secrets.rtsp_url;
				if (
					url?.operation === "replace" &&
					!/^rtsps?:\/\/[^\s]+$/.test(url.value)
				)
					fail(path, "Invalid RTSP URL");
			} else {
				if (has(item.secrets.rtsp_url))
					fail(path, "Stream URLs apply only to RTSP sources");
				if (f.segment_format !== "json")
					fail(path, "Telemetry sources require json segments");
				const axis = f.source_type.startsWith("axis_");
				const milestone = f.source_type === "milestone_event";
				if (axis && f.integration_id)
					fail(path, "AXIS sources use direct authentication");
				if (f.integration_id) {
					const linked = integrations.get(f.integration_id);
					if (
						!linked ||
						linked.fields.integration_type !==
							(milestone ? "milestone" : "homeassistant")
					)
						fail(path, "Integration is missing or has the wrong type");
					if (
						f.sensor_host ||
						has(item.secrets.sensor_password) ||
						(milestone && f.sensor_username)
					)
						fail(path, "Choose integration or direct credentials, not both");
				} else if (
					!f.sensor_host ||
					!has(item.secrets.sensor_password) ||
					((axis || milestone) && !f.sensor_username)
				)
					fail(path, "Complete the source host and credentials");
				if (milestone && f.sensor_port !== 443)
					fail(path, "Milestone uses port 443");
				if (f.source_type.startsWith("homeassistant_") && !f.ha_entity_id)
					fail(path, "Home Assistant requires an entity identifier");
			}
			if (f.source_type === "axis_scene_events") {
				if (!f.video_sensor_id || !f.zone_id)
					fail(path, "Scene events require a video sensor and zone");
			} else if (f.video_sensor_id)
				fail(path, "Video sensor association applies only to scene events");
		});
	});
export type EdgeConfigurationDraft = z.infer<
	typeof edgeConfigurationDraftSchema
>;
